import React, { useMemo, useState } from 'react';
import MobileReportOrientationManager from './reports/MobileReportOrientationManager';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart, Line, Sector
} from 'recharts';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import { standardizeSourceName, formatSourceDisplayName } from '../utils/reportAggregationUtils';
import { ReportData } from '../types/reportTypes';

// Colors for charts with improved accessibility and visual hierarchy
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFCC00', '#FF6666', '#5470C6', '#91CC75'
];

// Color scheme for source performance
const SOURCE_COLORS = {
  high: '#00C49F',    // Good ROI (green)
  medium: '#FFBB28',  // Medium ROI (yellow)
  low: '#FF8042',     // Low ROI (orange)
  negative: '#FF6666' // Negative ROI (red)
};

interface MarketingReportProps {
  data: ReportData;
  isLoading?: boolean;
  onExportPDF?: () => void;
  onFilter?: (filters: any) => void;
  hideHeader?: boolean; // Option to hide the header for embedded reports
}

const MarketingReport: React.FC<MarketingReportProps> = ({
  data,
  isLoading = false,
  onExportPDF,
  onFilter,
  hideHeader = false
}) => {
  // State for interactive elements
  const [activePieIndex, setActivePieIndex] = useState<number | null>(null);
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);
  const [activeSourceIndex, setActiveSourceIndex] = useState<number | null>(null);
  const [filterDate, setFilterDate] = useState<{start?: string, end?: string}>({});
  const [filterSource, setFilterSource] = useState<string | null>(null);

  // Active element handlers for enhanced interactivity
  const handlePieEnter = (_: any, index: number) => {
    setActivePieIndex(index);
  };

  const handlePieLeave = () => {
    setActivePieIndex(null);
  };

  const handleBarEnter = (_: any, index: number) => {
    setActiveBarIndex(index);
  };

  const handleBarLeave = () => {
    setActiveBarIndex(null);
  };

  const handleSourceClick = (data: any, index: number) => {
    setActiveSourceIndex(index === activeSourceIndex ? null : index);
    if (onFilter && data && data.activePayload && data.activePayload[0]) {
      const source = data.activePayload[0].payload.source;
      setFilterSource(source);
      onFilter({ source });
    }
  };

  // Custom active shape for pie chart for better interactivity
  const renderActiveShape = (props: any) => {
    const {
      cx, cy, innerRadius, outerRadius, startAngle, endAngle,
      fill, payload, percent, value
    } = props;

    return (
      <g>
        <text x={cx} y={cy} dy={-20} textAnchor="middle" fill={fill} className="text-lg font-semibold">
          {payload.trade}
        </text>
        <text x={cx} y={cy} dy={10} textAnchor="middle" fill="#333" className="text-base">
          {formatCurrency(value)}
        </text>
        <text x={cx} y={cy} dy={30} textAnchor="middle" fill="#999" className="text-sm">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius - 6}
          outerRadius={innerRadius - 2}
          fill={fill}
        />
      </g>
    );
  };

  // Custom tooltip formatter for better data display
  const customTooltipFormatter = (value: any, name: string) => {
    if (name === 'totalSales' || name === 'grossProfit' || name === 'adSpend' ||
        name === 'netProfit' || name === 'spend') {
      return [formatCurrency(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    if (name === 'roi') {
      return [`${value.toFixed(2)}x`, 'ROI'];
    }

    if (name.includes('%') || name === 'grossMarginPct') {
      return [formatPercentage(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    return [value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
  };

  // Function to apply color based on ROI performance
  const getROIColor = (roi: number) => {
    if (roi <= 0) return SOURCE_COLORS.negative;
    if (roi < 1) return SOURCE_COLORS.low;
    if (roi < 3) return SOURCE_COLORS.medium;
    return SOURCE_COLORS.high;
  };

  // Create date filter component
  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    if (onFilter && (filterDate.start || filterDate.end)) {
      onFilter({
        ...filterDate,
        source: filterSource
      });
    }
  };

  // Date filter component
  const DateFilterComponent = () => (
    <form onSubmit={handleDateFilter} className="flex items-center space-x-2 mb-4">
      <div>
        <label htmlFor="startDate" className="block text-sm text-gray-600">Start Date</label>
        <input
          type="date"
          id="startDate"
          className="border rounded px-2 py-1 text-sm"
          value={filterDate.start || ''}
          onChange={(e) => setFilterDate(prev => ({ ...prev, start: e.target.value }))}
        />
      </div>
      <div>
        <label htmlFor="endDate" className="block text-sm text-gray-600">End Date</label>
        <input
          type="date"
          id="endDate"
          className="border rounded px-2 py-1 text-sm"
          value={filterDate.end || ''}
          onChange={(e) => setFilterDate(prev => ({ ...prev, end: e.target.value }))}
        />
      </div>
      <button
        type="submit"
        className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm"
      >
        Apply Filter
      </button>
      {(filterDate.start || filterDate.end || filterSource) && (
        <button
          type="button"
          className="mt-5 bg-gray-200 hover:bg-gray-300 text-gray-800 py-1 px-3 rounded text-sm"
          onClick={() => {
            setFilterDate({});
            setFilterSource(null);
            setActiveSourceIndex(null);
            if (onFilter) onFilter({});
          }}
        >
          Clear Filters
        </button>
      )}
    </form>
  );

  // We need to declare all hooks before any conditional returns to follow React rules
  // All hooks need to be here at the top level
  // We now import standardizeSourceName from reportAggregationUtils

  // Calculate combined metrics for lead source performance
  const sourcePerformance = useMemo(() => {
    // Safety check for data
    if (!data?.adSpendBySource || !Array.isArray(data.adSpendBySource)) {
      return [];
    }
    
    // Create a Map of all sales sources for quick lookup
    const salesMap = new Map();
    (data.salesBySource || []).forEach(salesItem => {
      const standardizedSource = standardizeSourceName(salesItem.source);
      salesMap.set(standardizedSource, salesItem);
    });
    
    // Start with all sources that have ad spend and include sales data where available
    return data.adSpendBySource.map(adSpendItem => {
      const standardizedAdSource = standardizeSourceName(adSpendItem.source);
      const salesItem = salesMap.get(standardizedAdSource);
      
      // If we have sales data for this source, use it; otherwise use defaults
      const totalSales = salesItem ? salesItem.totalSales : 0;
      const count = salesItem ? salesItem.count : 0;
      const grossProfit = salesItem ? salesItem.grossProfit : 0;
      
      return {
        source: adSpendItem.source,
        totalSales: totalSales,
        count: count,
        grossProfit: grossProfit,
        adSpend: adSpendItem.spend,
        roi: grossProfit / (adSpendItem.spend || 1), // Avoid division by zero
        costPerSale: count > 0 ? adSpendItem.spend / count : adSpendItem.spend,
        costPerDollar: totalSales > 0 ? adSpendItem.spend / totalSales : 1
      };
    });
  }, [data?.salesBySource, data?.adSpendBySource]);
  
  // Sort sources by ROI
  const sortedSourcePerformance = useMemo(() => {
    if (!sourcePerformance || !sourcePerformance.length) {
      return [];
    }
    return [...sourcePerformance].sort((a, b) => b.roi - a.roi);
  }, [sourcePerformance]);
  
  // Calculate average values
  const averages = useMemo(() => {
    if (!data?.salesBySource || !data?.kpis) {
      return {
        avgSaleValue: 0,
        avgGrossProfit: 0,
        avgGrossProfitMargin: 0
      };
    }
    
    const totalJobs = (data.salesBySource || []).reduce((sum, item) => sum + (item.count || 0), 0);
    return {
      avgSaleValue: totalJobs > 0 ? (data.kpis.totalSales || 0) / totalJobs : 0,
      avgGrossProfit: totalJobs > 0 ? (data.kpis.grossProfit || 0) / totalJobs : 0,
      avgGrossProfitMargin: (data.kpis.totalSales || 0) > 0 ? (data.kpis.grossProfit || 0) / data.kpis.totalSales : 0
    };
  }, [data?.kpis, data?.salesBySource]);
  
  // Group job data by trade
  const jobsByTrade = useMemo(() => {
    if (!data?.jobData || !Array.isArray(data.jobData)) {
      return {
        "Roofing": [],
        "Windows & Doors": [],
        "Other": []
      };
    }
    
    return {
      "Roofing": (data.jobData || []).filter(job => job.trade === "Roofing"),
      "Windows & Doors": (data.jobData || []).filter(job => job.trade === "Windows & Doors"),
      "Other": (data.jobData || []).filter(job => job.trade !== "Roofing" && job.trade !== "Windows & Doors")
    };
  }, [data?.jobData]);

  // Format month name or date range for YTD
  const monthName = useMemo(() => {
    if (!data?.period) return "Report";

    // Check if this is a YTD report
    if (data.period.ytd && data.period.startMonth && data.period.endMonth) {
      try {
        const startMonth = new Date(2000, data.period.startMonth - 1)
          .toLocaleString('default', { month: 'long' });
        const endMonth = new Date(2000, data.period.endMonth - 1)
          .toLocaleString('default', { month: 'long' });
        return `${startMonth}-${endMonth}`;
      } catch (error) {
        console.error('Error formatting YTD date range:', error);
        return "YTD Report";
      }
    }

    // Regular monthly report
    if (data.period.month) {
      try {
        return new Date(2000, data.period.month - 1)
          .toLocaleString('default', { month: 'long' });
      } catch (error) {
        console.error('Error formatting month:', error);
        return "Report";
      }
    }

    return "Report";
  }, [data?.period]);
  
  // Safety check for data - only after all hooks are defined
  if (!data) {
    return (
      <div className="bg-white p-6 font-sans">
        <div className="p-6 bg-red-50 text-red-700 rounded-lg">
          <h3 className="text-xl font-bold mb-2">No Data Available</h3>
          <p>This report has no data to display. Please try uploading new data files.</p>
        </div>
      </div>
    );
  }

  // Loading UI
  if (isLoading) {
    return (
      <div className="bg-white p-6 font-sans">
        <div className="flex flex-col items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-xl text-gray-600">Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <MobileReportOrientationManager>
      <div className="bg-white p-6 font-sans">
      {!hideHeader && (
        <div className="flex justify-between items-center mb-6">
          <div className="text-center flex-grow">
            <img
              src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png"
              alt="ROI Exteriors Logo"
              className="mx-auto h-20 mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-800">ROI Exteriors Marketing Performance Report</h1>
            <h2 className="text-xl text-gray-600">
              {data.period ? `${monthName} ${data.period.year}` : "Period Report"}
            </h2>
          </div>

          {/* Report actions */}
          <div className="flex flex-col space-y-2">
            {onExportPDF && (
              <button
                onClick={onExportPDF}
                className="print-button bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                Export PDF
              </button>
            )}

          </div>
        </div>
      )}

      {/* Filter controls */}
      {onFilter && <DateFilterComponent />}

      {filterSource && (
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <p className="text-blue-700 font-medium">
            Filtered by source: <span className="font-bold">{filterSource}</span>
            <button
              onClick={() => {
                setFilterSource(null);
                if (onFilter) onFilter({ ...filterDate });
              }}
              className="ml-2 text-sm text-blue-700 hover:text-blue-900"
            >
              ×
            </button>
          </p>
        </div>
      )}
      
      {/* KPI Overview Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Key Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Total Sales</div>
            <div className="text-2xl font-bold text-blue-800">{formatCurrency(data.kpis?.totalSales || 0)}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Gross Profit</div>
            <div className="text-2xl font-bold text-green-800">{formatCurrency(data.kpis?.grossProfit || 0)}</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Ad Spend</div>
            <div className="text-2xl font-bold text-orange-800">{formatCurrency(data.kpis?.adSpend || 0)}</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Net Profit</div>
            <div className="text-2xl font-bold text-purple-800">{formatCurrency(data.kpis?.netProfit || 0)}</div>
          </div>
        </div>
        <div className="mt-4 text-center">
          <div className="text-lg font-semibold">Net Profit Margin: <span className="text-blue-700">{formatPercentage(data.kpis?.netProfitPercent || 0)}</span></div>
          <div className="text-lg font-semibold">Gross Profit Margin: <span className="text-green-700">{formatPercentage(data.kpis?.grossProfitPercent || 0)}</span></div>
        </div>
      </div>
      
      {/* Trade Categories Performance */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Sales by Trade Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.salesByTrade || []}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                onMouseMove={handleBarEnter}
                onMouseLeave={handleBarLeave}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="trade" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar
                  dataKey="totalSales"
                  name="Sales"
                  fill="#0088FE"
                  animationDuration={750}
                  animationBegin={0}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="grossProfit"
                  name="Gross Profit"
                  fill="#00C49F"
                  animationDuration={750}
                  animationBegin={250}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.salesByTrade}
                  dataKey="totalSales"
                  nameKey="trade"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={activePieIndex !== null ? 40 : 0}
                  activeIndex={activePieIndex === null ? undefined : activePieIndex}
                  activeShape={renderActiveShape}
                  onMouseEnter={handlePieEnter}
                  onMouseLeave={handlePieLeave}
                  animationBegin={0}
                  animationDuration={800}
                >
                  {data.salesByTrade.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke={activePieIndex === index ? '#fff' : 'none'}
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    display: activePieIndex !== null ? 'none' : 'block'
                  }}
                />
                <Legend
                  formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                  iconSize={10}
                  wrapperStyle={{ paddingTop: 20 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left">Trade Category</th>
                  <th className="py-2 px-4 text-right">Sales Count</th>
                  <th className="py-2 px-4 text-right">Total Sales</th>
                  <th className="py-2 px-4 text-right">Gross Profit</th>
                  <th className="py-2 px-4 text-right">Avg Sale Value</th>
                  <th className="py-2 px-4 text-right">Gross Margin %</th>
                </tr>
              </thead>
              <tbody>
                {data.salesByTrade.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4">{item.trade}</td>
                    <td className="py-2 px-4 text-right">{item.count}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.totalSales)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.grossProfit)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.totalSales / item.count)}</td>
                    <td className="py-2 px-4 text-right">{formatPercentage(item.grossProfit / item.totalSales)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-200 font-bold">
                  <td className="py-2 px-4">TOTAL</td>
                  <td className="py-2 px-4 text-right">{data.salesByTrade.reduce((sum, item) => sum + item.count, 0)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(data.kpis.totalSales)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(data.kpis.grossProfit)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(averages.avgSaleValue)}</td>
                  <td className="py-2 px-4 text-right">{formatPercentage(averages.avgGrossProfitMargin)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Ad Spend Distribution */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Ad Spend Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(data.adSpendBySource || []).filter(item => item?.spend > 0).sort((a, b) => b.spend - a.spend)}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 150, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                <YAxis 
                  dataKey="source" 
                  type="category" 
                  tick={{ fontSize: 12 }}
                  width={140}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value as number), 'Ad Spend']}
                  contentStyle={{ borderRadius: '5px' }}
                />
                <Bar dataKey="spend" name="Ad Spend" fill="#FF8042" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(data.adSpendBySource || []).filter(item => item?.spend > 500)}
                  dataKey="spend"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ source, percent }) => `${source}: ${(percent as number * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {(data.adSpendBySource || []).filter(item => item?.spend > 500).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Lead Source Performance */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Lead Source Performance</h2>
        <div className="mb-8">
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={sortedSourcePerformance}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                onClick={handleSourceClick}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="source"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(value) => formatCurrency(value)}
                  label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, Math.max(5, ...sortedSourcePerformance.map(s => s.roi) as number[]) + 1]}
                  label={{ value: 'ROI', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                  cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                />
                <Legend
                  verticalAlign="top"
                  wrapperStyle={{ paddingBottom: 10 }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="totalSales"
                  name="Sales"
                  fill="#0088FE"
                  animationDuration={750}
                  animationBegin={0}
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                />
                <Bar
                  yAxisId="left"
                  dataKey="adSpend"
                  name="Ad Spend"
                  fill="#FF8042"
                  animationDuration={750}
                  animationBegin={250}
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="roi"
                  name="ROI"
                  stroke="#8884d8"
                  strokeWidth={3}
                  dot={(props: any) => {
                    const { cx, cy, payload } = props;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={payload.roi > 3 ? 8 : payload.roi > 1 ? 6 : 4}
                        fill={getROIColor(payload.roi)}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    );
                  }}
                  activeDot={{ r: 8, stroke: '#8884d8', strokeWidth: 3, fill: '#fff' }}
                  animationDuration={1000}
                  animationBegin={500}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center mt-2 space-x-6 mb-4">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.high }}></div>
              <span className="text-sm">High ROI (&gt;3x)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.medium }}></div>
              <span className="text-sm">Medium ROI (1-3x)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.low }}></div>
              <span className="text-sm">Low ROI (&lt;1x)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.negative }}></div>
              <span className="text-sm">Negative ROI</span>
            </div>
          </div>

          <p className="mt-2 text-gray-600 text-sm text-center mb-4">
            <span className="font-medium">Pro Tip:</span> Click on any lead source in the chart to filter the report
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 text-left">Lead Source</th>
                <th className="py-2 px-4 text-right">Sales Count</th>
                <th className="py-2 px-4 text-right">Total Sales</th>
                <th className="py-2 px-4 text-right">Ad Spend</th>
                <th className="py-2 px-4 text-right">Gross Profit</th>
                <th className="py-2 px-4 text-right">ROI</th>
                <th className="py-2 px-4 text-right">Cost per Sale</th>
              </tr>
            </thead>
            <tbody>
              {sortedSourcePerformance.map((source, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-2 px-4">{source.source}</td>
                  <td className="py-2 px-4 text-right">{source.count}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.totalSales)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.adSpend)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.grossProfit)}</td>
                  <td className="py-2 px-4 text-right">{source.roi.toFixed(2)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.costPerSale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Executive Summary */}
      <div className="mb-12 p-6 bg-gray-50 rounded-lg">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Executive Summary</h2>
        <div className="space-y-3">
          <p>
            For {monthName} {data.period?.year}, ROI Exteriors achieved <strong>{formatCurrency(data.kpis.totalSales)}</strong> in total sales with 
            a gross profit of <strong>{formatCurrency(data.kpis.grossProfit)}</strong>. The total ad spend was 
            <strong> {formatCurrency(data.kpis.adSpend)}</strong>, sales commissions and fees amounted to <strong>{formatCurrency(data.kpis.salesCommissions + data.kpis.managerFees + data.kpis.companyOH)}</strong>,
            resulting in a net profit of 
            <strong> {formatCurrency(data.kpis.netProfit)}</strong> and a net profit margin of 
            <strong> {formatPercentage(data.kpis.netProfitPercent)}</strong>.
          </p>
          
          <p>
            <strong>Top Performing Lead Sources:</strong> The highest ROI came from 
            {sortedSourcePerformance.length > 0 ? ` ${sortedSourcePerformance[0].source}` : ' organic sources'}, 
            followed by {sortedSourcePerformance.length > 1 ? sortedSourcePerformance[1].source : 'referrals'}.
            {sortedSourcePerformance.some(s => s.roi < 1) && 
              ` Some lead sources are showing an ROI below 1, indicating they're not generating sufficient returns.`}
          </p>
          
          <p>
            <strong>Trade Category Performance:</strong> The highest performing trade category was 
            <strong> Roofing</strong> at <strong>{formatCurrency(data.salesByTrade[0].totalSales)}</strong> in sales, 
            followed by <strong>Windows & Doors</strong> at <strong>{formatCurrency(data.salesByTrade[1].totalSales)}</strong>.
            The gross profit values reflect our updated profit calculations, with Roofing generating approximately
            <strong> {formatCurrency(data.salesByTrade[0].grossProfit)}</strong> in gross profit and Windows & Doors generating approximately
            <strong> {formatCurrency(data.salesByTrade[1].grossProfit)}</strong> in gross profit.
          </p>
          
          <p>
            <strong>Recommendations:</strong> 
            {sortedSourcePerformance.some(s => s.adSpend > 0 && s.roi < 1) ? 
              ` Consider reallocating budget from underperforming sources like ${
                sortedSourcePerformance.filter(s => s.adSpend > 0 && s.roi < 1)
                  .map(s => s.source)
                  .slice(0, 2)
                  .join(' and ')
              } to higher ROI channels.` : 
              ' Continue investing in current high-performing channels while testing new markets.'}
            {` Focus on the ${
              [...data.salesByTrade].sort((a, b) => (b.grossProfit/b.totalSales) - (a.grossProfit/a.totalSales))[0].trade
            } category which has the highest profit margin.`}
          </p>
        </div>
      </div>
      
      {/* Forward Looking Analysis */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Forward Looking Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold mb-3">Marketing ROI Optimization</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>High Performers:</strong> {
                  sortedSourcePerformance.filter(s => s.roi > 3).slice(0, 2).map(s => s.source).join(", ")
                } show exceptional ROI and should receive continued investment.
              </li>
              <li>
                <strong>Underperformers:</strong> {
                  sortedSourcePerformance.filter(s => s.adSpend > 0 && s.roi < 1.5).slice(0, 2).map(s => s.source).join(", ")
                } need further analysis or budget reallocation.
              </li>
              <li>
                <strong>Opportunity Areas:</strong> Consider increasing budget for {
                  sortedSourcePerformance.filter(s => s.roi > 2.5 && s.count < 3).slice(0, 2).map(s => s.source).join(", ")
                } to scale success.
              </li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold mb-3">Product Strategy</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Primary Focus:</strong> Roofing represents {
                  formatPercentage(data.salesByTrade[0].totalSales / data.kpis.totalSales)
                } of total sales and should remain a core focus.
              </li>
              <li>
                <strong>Growth Opportunity:</strong> {
                  [...data.salesByTrade].sort((a, b) => (b.grossProfit/b.totalSales) - (a.grossProfit/a.totalSales))[0].trade
                } shows the highest margin at {
                  formatPercentage([...data.salesByTrade].sort((a, b) => (b.grossProfit/b.totalSales) - (a.grossProfit/a.totalSales))[0].grossProfit / 
                  [...data.salesByTrade].sort((a, b) => (b.grossProfit/b.totalSales) - (a.grossProfit/a.totalSales))[0].totalSales)
                } and merits expansion efforts.
              </li>
              <li>
                <strong>Bundle Opportunities:</strong> Consider package offers combining high-frequency services with complementary products.
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Individual Job Analysis */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Individual Job Analysis</h2>
        
        {/* Roofing Jobs */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold py-2 px-4 bg-blue-50 rounded mb-4">
            Roofing Jobs ({jobsByTrade["Roofing"].length} jobs, {formatCurrency(data.salesByTrade[0].totalSales)} total revenue)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left">Job Info</th>
                  <th className="py-2 px-4 text-left">Lead Source</th>
                  <th className="py-2 px-4 text-right">Contract Total</th>
                  <th className="py-2 px-4 text-right">Gross Margin %</th>
                  <th className="py-2 px-4 text-right">Gross Profit</th>
                  <th className="py-2 px-4 text-right">Sales Commission</th>
                  <th className="py-2 px-4 text-right">Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {jobsByTrade["Roofing"].map((job, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4">{job.jobInfo}</td>
                    <td className="py-2 px-4">{job.leadSource}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                    <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.salesCommission)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Windows & Doors Jobs */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold py-2 px-4 bg-green-50 rounded mb-4">
            Windows & Doors Jobs ({jobsByTrade["Windows & Doors"].length} jobs, {formatCurrency(data.salesByTrade[1].totalSales)} total revenue)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left">Job Info</th>
                  <th className="py-2 px-4 text-left">Lead Source</th>
                  <th className="py-2 px-4 text-right">Contract Total</th>
                  <th className="py-2 px-4 text-right">Gross Margin %</th>
                  <th className="py-2 px-4 text-right">Gross Profit</th>
                  <th className="py-2 px-4 text-right">Sales Commission</th>
                  <th className="py-2 px-4 text-right">Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {jobsByTrade["Windows & Doors"].map((job, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4">{job.jobInfo}</td>
                    <td className="py-2 px-4">{job.leadSource}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                    <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.salesCommission)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Other Jobs */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold py-2 px-4 bg-orange-50 rounded mb-4">
            Other Jobs ({jobsByTrade["Other"].length} jobs, {formatCurrency(jobsByTrade["Other"].reduce((sum, job) => sum + job.contractTotal, 0))} total revenue)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left">Job Info</th>
                  <th className="py-2 px-4 text-left">Lead Source</th>
                  <th className="py-2 px-4 text-left">Trade</th>
                  <th className="py-2 px-4 text-right">Contract Total</th>
                  <th className="py-2 px-4 text-right">Gross Margin %</th>
                  <th className="py-2 px-4 text-right">Gross Profit</th>
                  <th className="py-2 px-4 text-right">Sales Commission</th>
                  <th className="py-2 px-4 text-right">Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {jobsByTrade["Other"].map((job, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4">{job.jobInfo}</td>
                    <td className="py-2 px-4">{job.leadSource}</td>
                    <td className="py-2 px-4">{job.trade}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                    <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.salesCommission)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-semibold mb-3">Individual Job Insights</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Highest Value Jobs:</strong> The Facebook/Meta Ads client (William & Tyla Liggins, $26,921), Tara & Alford Bell from Thumbtack ($19,700), and David & Gail Cantor via Word of Mouth referral ($18,734) represent the largest individual contracts.</li>
            <li><strong>Best Profit Margins:</strong> Previous Customer job (56.1%), Angi lead for Tyler & Elea Fogarty (53.8%), and Google Organic Search lead for Quinn & Jillian Gooden (50.0%) delivered the highest gross margins.</li>
            <li><strong>Job Size Distribution:</strong> 6 jobs were over $15,000, 10 jobs were between $7,000-$15,000, and 6 jobs were under $7,000.</li>
            <li><strong>Underperforming Jobs:</strong> The lowest margin job was Eric & Allison Boiter via Yelp at 36.5% - still profitable but below average for the month.</li>
          </ul>
        </div>
      </div>
      
      <div className="text-center text-gray-600 text-sm mt-8">
        <p>Generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        <p>ROI Exteriors Marketing Performance Report</p>
      </div>
    </div>
    </MobileReportOrientationManager>
  );
};

export default MarketingReport;